/**
 * 安全的本地儲存工具
 * 提供加密和解密功能，增強 localStorage 的安全性
 */

import CryptoJS from "crypto-js";

// 應用程式前綴，用於區分不同應用程式的儲存項目
const APP_PREFIX = "ortix_";

// 加密密鑰，理想情況下應從環境變數獲取
// 在實際生產環境中，應該使用環境變數或其他安全的方式來存儲密鑰
const getEncryptionKey = (): string => {
  return (
    import.meta.env.VITE_STORAGE_ENCRYPTION_KEY ||
    "default-secure-key-change-in-production"
  );
};

/**
 * 安全儲存工具
 * 提供加密的 localStorage 操作方法
 */
export const secureStorage = {
  /**
   * 設置加密的項目到 localStorage
   * @param key 儲存鍵名
   * @param value 要儲存的值
   */
  setItem: (key: string, value: any): void => {
    try {
      // 轉換為字串
      const valueToStore =
        typeof value === "object" ? JSON.stringify(value) : String(value);

      // 加密資料
      const encryptedValue = CryptoJS.AES.encrypt(
        valueToStore,
        getEncryptionKey(),
      ).toString();

      // 儲存到 localStorage，加上前綴
      localStorage.setItem(`${APP_PREFIX}${key}`, encryptedValue);
    } catch (error) {
      console.error("安全儲存失敗:", error);
    }
  },

  /**
   * 從 localStorage 獲取並解密項目
   * @param key 儲存鍵名
   * @param defaultValue 預設值（如果項目不存在或解密失敗）
   * @returns 解密後的值或預設值
   */
  getItem: <T>(key: string, defaultValue?: T): T | string | null => {
    try {
      // 從 localStorage 獲取加密的值
      const encryptedValue = localStorage.getItem(`${APP_PREFIX}${key}`);

      if (!encryptedValue) {
        return defaultValue !== undefined ? defaultValue : null;
      }

      // 解密資料
      const decryptedBytes = CryptoJS.AES.decrypt(
        encryptedValue,
        getEncryptionKey(),
      );

      const decryptedValue = decryptedBytes.toString(CryptoJS.enc.Utf8);

      // 嘗試解析為 JSON，如果失敗則返回原始字串
      try {
        return JSON.parse(decryptedValue);
      } catch {
        return decryptedValue;
      }
    } catch (error) {
      console.error("安全讀取失敗:", error);
      return defaultValue !== undefined ? defaultValue : null;
    }
  },

  /**
   * 從 localStorage 中移除項目
   * @param key 儲存鍵名
   */
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(`${APP_PREFIX}${key}`);
    } catch (error) {
      console.error("安全移除失敗:", error);
    }
  },

  /**
   * 清除所有以應用程式前綴開頭的項目
   */
  clear: (): void => {
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(APP_PREFIX))
        .forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.error("安全清除失敗:", error);
    }
  },

  /**
   * 檢查項目是否存在
   * @param key 儲存鍵名
   * @returns 是否存在
   */
  hasItem: (key: string): boolean => {
    return localStorage.getItem(`${APP_PREFIX}${key}`) !== null;
  },
};
